import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  IconUser,
  IconShield,
  IconTicket,
  IconUserPlus,
  IconUserMinus,
  IconFileExport,
  IconEye,
  IconSearch,
  IconClock,
  IconActivity
} from '@tabler/icons-react';

const AdminActivityLog = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await api.get('/api/admin/activity-log', {
          params: { limit: 50, offset: page * 50 }
        });

        if (page === 0) {
          setActivities(response.data.activities);
        } else {
          setActivities(prev => [...prev, ...response.data.activities]);
        }

        setHasMore(response.data.activities.length === 50);
      } catch (error) {
        console.error('Failed to fetch activity log:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [page]);

  const formatAction = (action) => {
    const actionMap = {
      'view_users': 'Viewed users list',
      'impersonate': 'Impersonated user',
      'generate_invite_code': 'Generated invite code',
      'view_invite_codes': 'Viewed invite codes',
      'view_stats': 'Viewed platform stats',
      'change_user_role': 'Changed user role',
      'delete_user': 'Deleted user',
      'export_data': 'Exported user data',
      'login': 'Logged in',
      'search_execution': 'Executed search batch'
    };
    return actionMap[action] || action;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;

    // If less than a minute
    if (diff < 60000) {
      return 'Just now';
    }
    // If less than an hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    // If less than a day
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    // If less than a week
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }

    // Otherwise show full date
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'impersonate':
        return <IconUser size={16} className="text-yellow-500" />;
      case 'generate_invite_code':
        return <IconTicket size={16} className="text-green-500" />;
      case 'change_user_role':
        return <IconShield size={16} className="text-purple-500" />;
      case 'delete_user':
        return <IconUserMinus size={16} className="text-red-500" />;
      case 'export_data':
        return <IconFileExport size={16} className="text-blue-500" />;
      case 'view_users':
      case 'view_invite_codes':
      case 'view_stats':
        return <IconEye size={16} className="text-gray-500" />;
      case 'search_execution':
        return <IconSearch size={16} className="text-indigo-500" />;
      default:
        return <IconActivity size={16} className="text-gray-400" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'impersonate':
        return 'border-yellow-200 dark:border-yellow-700/40 bg-yellow-50 dark:bg-yellow-900/20';
      case 'generate_invite_code':
        return 'border-green-200 dark:border-green-700/40 bg-green-50 dark:bg-green-900/20';
      case 'change_user_role':
        return 'border-purple-200 dark:border-purple-700/40 bg-purple-50 dark:bg-purple-900/20';
      case 'delete_user':
        return 'border-red-200 dark:border-red-700/40 bg-red-50 dark:bg-red-900/20';
      case 'export_data':
        return 'border-blue-200 dark:border-blue-700/40 bg-blue-50 dark:bg-blue-900/20';
      case 'search_execution':
        return 'border-indigo-200 dark:border-indigo-700/40 bg-indigo-50 dark:bg-indigo-900/20';
      default:
        return 'border-gray-200 dark:border-[#2e2e2e] bg-gray-50 dark:bg-[#262626]';
    }
  };

  const filterTabs = [
    { id: 'all', label: t('admin.activity.filters.all') },
    { id: 'admin', label: t('admin.activity.filters.admin') },
    { id: 'search', label: t('admin.activity.filters.search') },
    { id: 'users', label: 'User Management' }
  ];

  const filterActivities = (activities) => {
    if (filter === 'all') return activities;
    if (filter === 'admin') {
      return activities.filter(a =>
        ['impersonate', 'generate_invite_code', 'change_user_role', 'delete_user'].includes(a.action)
      );
    }
    if (filter === 'search') {
      return activities.filter(a => a.action === 'search_execution');
    }
    if (filter === 'users') {
      return activities.filter(a =>
        ['view_users', 'export_data'].includes(a.action)
      );
    }
    return activities;
  };

  const groupActivitiesByDate = (activities) => {
    const groups = {};
    activities.forEach(activity => {
      const date = new Date(activity.created_at);
      const dateKey = date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    return groups;
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d8f9b8]"></div>
      </div>
    );
  }

  const filteredActivities = filterActivities(activities);
  const groupedActivities = groupActivitiesByDate(filteredActivities);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg">
        {/* Header with Filter Tabs */}
        <div className="p-6 border-b dark:border-[#2e2e2e]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-[#f5f5f5]">
              {t('admin.activity.title')}
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-[#999999]">
              <IconClock size={16} />
              <span>Last 30 days</span>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            {filterTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === tab.id
                    ? 'bg-[#d8f9b8] text-gray-900'
                    : 'bg-gray-100 dark:bg-[#262626] text-gray-600 dark:text-[#c4c4c4] hover:bg-gray-200 dark:hover:bg-[#2e2e2e]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Timeline Content */}
        <div className="p-6">
          {Object.keys(groupedActivities).length === 0 ? (
            <div className="text-center py-12">
              <IconActivity size={48} className="mx-auto text-gray-300 dark:text-[#2e2e2e] mb-4" />
              <p className="text-gray-600 dark:text-[#c4c4c4]">{t('admin.activity.noActivity')}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedActivities).map(([date, activities]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-px bg-gray-200 dark:bg-[#2e2e2e] flex-1"></div>
                    <span className="text-sm font-medium text-gray-500 dark:text-[#999999] px-3 bg-white dark:bg-[#1f1f1f]">
                      {date}
                    </span>
                    <div className="h-px bg-gray-200 dark:bg-[#2e2e2e] flex-1"></div>
                  </div>

                  {/* Timeline Items */}
                  <div className="relative">
                    {/* Vertical Line */}
                    <div className="absolute left-5 top-8 bottom-0 w-0.5 bg-gray-200 dark:bg-[#2e2e2e]"></div>

                    <div className="space-y-4">
                      {activities.map((activity, index) => (
                        <div key={activity.id} className="relative flex gap-4">
                          {/* Timeline Dot */}
                          <div className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 ${getActionColor(activity.action)}`}>
                            {getActionIcon(activity.action)}
                          </div>

                          {/* Content Card */}
                          <div className="flex-1 pb-4">
                            <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  {/* Action Title */}
                                  <p className="font-medium text-gray-900 dark:text-[#f5f5f5]">
                                    {formatAction(activity.action)}
                                  </p>

                                  {/* Meta Info */}
                                  <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mt-1">
                                    by <span className="font-medium">{activity.admin_email || 'System'}</span>
                                    {activity.target_email && (
                                      <span>
                                        {' → '}
                                        <span className="font-medium">{activity.target_email}</span>
                                      </span>
                                    )}
                                  </p>

                                  {/* Additional Metadata */}
                                  {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      {activity.metadata.code && (
                                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-[#262626] rounded text-xs">
                                          <IconTicket size={12} />
                                          <span className="font-mono">{activity.metadata.code}</span>
                                        </div>
                                      )}
                                      {activity.metadata.newRole && (
                                        <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">
                                          Role changed to: <span className="font-medium text-purple-600">{activity.metadata.newRole}</span>
                                        </div>
                                      )}
                                      {activity.metadata.queriesCount && (
                                        <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">
                                          Queries: <span className="font-medium">{activity.metadata.queriesCount}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Timestamp */}
                                <span className="text-xs text-gray-500 dark:text-[#999999] whitespace-nowrap ml-4">
                                  {formatDate(activity.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && filteredActivities.length > 0 && (
            <div className="mt-8 text-center">
              <button
                onClick={() => setPage(page + 1)}
                className="px-6 py-2.5 bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] rounded-lg text-sm font-medium text-gray-700 dark:text-[#c4c4c4] hover:bg-gray-50 dark:hover:bg-[#262626] transition-colors inline-flex items-center gap-2"
              >
                <IconClock size={16} />
                Load More Activities
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActivityLog;